import React, {useCallback, useEffect, useMemo} from 'react';
import {connect} from "react-redux";
import {
    Box,
    Button, Checkbox,
    CircularProgress, LinearProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel, FormGroup,
    FormHelperText, IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {useHistory, useLocation, useParams} from "react-router-dom";
import {Controller, useFieldArray, useForm, useWatch} from "react-hook-form";
import {alertActions} from "../../store/actions/alertActions";
import {automationRulesAction} from "../../store/actions/automationRulesAction";
import TrafficSourceSelect from "../campaigns/TrafficSourceSelect";
import {DeleteOutline, Add} from "@mui/icons-material";
import {integrationService} from "../../service/integrationService";
import CampaignSelect, {MAX_SELECTED_CAMPAIGNS} from "./CampaignSelect";
import DateTimeRangePicker from "./DateTimeRangePicker";
import {workspaceActions} from "../../store/actions/workspaceAction";
import {trafficSourceServices} from "../../service/trafficSourceService";
import {campaignServices} from "../../service/campaignService";
import {customEventActions} from "../../store/actions/customEventActions";

import {AutomationDialog, AutomationSection, automationFieldGrid, automationSelectMenuProps, automationSelectPaperStyles} from './AutomationDesign';
import {SettingsDialogDescription} from '../settings/SettingsDialog';
import {getSettingsColors} from '../settings/SettingsDesign';

const ENTITY_TYPES = [
    {
        value: "campaign",
        label: "Campaigns"
    },
    {
        value: "adset",
        label: "Ad sets/groups"
    },
    {
        value: "ad",
        label: "Ads"
    }
];
const METRICS = [
    {
        value: "visits_sum",
        label: "Visits"
    },
    {
        value: "clicks_sum",
        label: "Clicks"
    },
    {
        value: "revenue_sum",
        label: "Revenue"
    },
    {
        value: "cost_sum",
        label: "Cost"
    },
    {
        value: "profit_sum",
        label: "Profit"
    },
    {
        value: "conversions_approved_sum",
        label: "Conversions Approved"
    },
    {
        value: "conversions_all_sum",
        label: "Conversions All"
    },
    {
        value: "roi_percent_avg",
        label: "ROI, % (profit/cost*100)"
    },
    {
        value: "roas_avg",
        label: "ROAS (revenue/cost)"
    },
    {
        value: "roas_percent_avg",
        label: "ROAS, % (revenue/cost*100)"
    },
    {
        value: "cpa_avg",
        label: "CPA"
    },
    {
        value: "epc_avg",
        label: "EPC"
    },
    {
        value: "cpc_avg",
        label: "CPC"
    },
    {
        value: "cpv_avg",
        label: "CPV"
    },
    {
        value: "ctr_percent_avg",
        label: "LP CTR, %"
    },
    {
        value: "conversion_rate_percent_avg",
        label: "CR, %"
    }
];
const OPERATORS = [
    {
        value: ">",
        label: "is greater than"
    },
    {
        value: "<",
        label: "is less than"
    },
    {
        value: "=",
        label: "is equal to"
    },
    {
        value: "!=",
        label: "is not equal to"
    }
];
const PERIODS = [
    {
        value: "today",
        label: "Today"
    },
    {
        value: "yesterday",
        label: "Yesterday"
    },
    {
        value: "24h",
        label: "Last 24 hours"
    },
    {
        value: "3d",
        label: "Last 3 days"
    },
    {
        value: "5d",
        label: "Last 5 days"
    },
    {
        value: "7d",
        label: "Last 7 days"
    },
    {
        value: "14d",
        label: "Last 14 days"
    },
    {
        value: "30d",
        label: "Last 30 days"
    }
];
const FREQUENCIES = [
    {
        value: 15,
        label: "Every 15 minutes"
    },
    {
        value: 30,
        label: "Every 30 minutes"
    },
    {
        value: 60,
        label: "Every 1 hour"
    },
    {
        value: 180,
        label: "Every 3 hours"
    },
    {
        value: 360,
        label: "Every 6 hours"
    },
    {
        value: 720,
        label: "Every 12 hours (default)"
    },
    {
        value: 1440,
        label: "Daily"
    },
    {
        value: 4320,
        label: "Every 3 days"
    },
    {
        value: 7200,
        label: "Every 5 days"
    },
    {
        value: 10080,
        label: "Every 7 days"
    },
    {
        value: 20160,
        label: "Every 14 days"
    }
];
const ACTIONS = [
    {
        value: "pause",
        label: "Pause",
        group: "Campaign controls"
    },
    {
        value: "enable",
        label: "Enable",
        group: "Campaign controls"
    },
    {
        value: "increase_daily_budget",
        label: "Increase daily budget",
        group: "Campaign controls"
    },
    {
        value: "decrease_daily_budget",
        label: "Decrease daily budget",
        group: "Campaign controls"
    },
    {
        value: "none",
        label: "Send notification only",
        group: "Campaign controls"
    }
];
const defaultCondition = {
    metric: "cost_sum",
    operator: ">",
    value: "",
    period: "7d"
};
const defaultAction = {
    action: "pause",
    parameters: {
        type: "percent",
        value: "",
        maxDailyBudget: "",
        minDailyBudget: ""
    }
};

const cloneDefaultSchedule = () => {
    return DEFAULT_SCHEDULE.map((day) => ({
        ...day,
        ranges: day.ranges.map((range) => ({
            ...range
        }))
    }));
};

const createDefaultFormValues = () => ({
    name: "",
    workspace: null,
    trafficSource: null,
    entityType: "campaign",
    targetMode: "all",
    campaign: [],
    onlyActiveCampaigns: true,
    ruleLogic: "AND",
    settings: {
        notifications: {
            inApp: false
        }
    },
    triggerType: "metrics",
    conditions: [
        {...defaultCondition}
    ],
    intervalMinutes: 720,
    metrics: {
        type: "metrics",
        adAccountId: "",
        timezone: "Etc/UTC",
        currency: "USD",
        runWindow: {
            type: "always",
            dateTimeRange: {
                from: "",
                to: ""
            }
        }
    },
    schedule: {
        cronDays: [],
        time: "",
        days: cloneDefaultSchedule(),
        timezone: "Etc/UTC"
    },
    actions: [
        {
            action: defaultAction.action,
            parameters: {
                ...defaultAction.parameters
            }
        }
    ]
});

const parseScheduleCron = (cron) => {
    if (!cron) {
        return {
            days: [],
            time: ""
        };
    }

    const parts = cron.trim().split(/\s+/);

    if (parts.length !== 5) {
        return {
            days: [],
            time: ""
        };
    }

    const [minute, hour, , , weekDays] = parts;

    const minuteNumber = Number(minute);
    const hourNumber = Number(hour);

    if (
        !Number.isInteger(minuteNumber) ||
        !Number.isInteger(hourNumber) ||
        minuteNumber < 0 ||
        minuteNumber > 59 ||
        hourNumber < 0 ||
        hourNumber > 23
    ) {
        return {
            days: [],
            time: ""
        };
    }

    const days = weekDays
        .split(",")
        .map(Number)
        .filter((day) =>
            Number.isInteger(day) &&
            day >= 0 &&
            day <= 6
        );

    return {
        days,
        time:
            `${String(hourNumber).padStart(2, "0")}:` +
            `${String(minuteNumber).padStart(2, "0")}`
    };
};

const mapTimeRangesToScheduleDays = (timeRanges = []) => {
    return DAYS.map((day) => {
        const ranges = timeRanges
            .filter((range) => Number(range?.weekDay) === day.value).map((range) => ({
                from: range?.startTime || "00:00",
                to: range?.endTime || "24:00"
            }));

        return {
            day: day.value,
            enabled: ranges.length > 0,
            ranges:
                ranges.length > 0
                    ? ranges
                    : [
                        {
                            from: "00:00",
                            to: "24:00"
                        }
                    ]
        };
    });
};

const mapRuleToForm = (rule, userWorkspaces = []) => {

    const values = createDefaultFormValues();

    if (!rule) {
        return values;
    }

    /*
     * Basic values
     */
    values.name = rule?.name || "";
    values.entityType = rule?.entityType || "campaign";
    values.targetMode = rule?.targetMode || "all";
    values.ruleLogic = rule?.ruleLogic || "AND";
    values.settings = {
        ...rule?.settings,
        notifications: {
            ...rule?.settings?.notifications,
            inApp: Boolean(rule?.settings?.notifications?.inApp)
        }
    };

    /*
     * Workspace
     */
    const workspace = userWorkspaces.find((item) => Number(item?.workspaceId) === Number(rule?.workspaceId));
    values.workspace = workspace || null;

    /*
     * Conditions
     */
    if (Array.isArray(rule?.conditions) && rule.conditions.length > 0) {
        values.conditions = rule.conditions.map(
            (condition) => ({
                metric: condition?.metric || defaultCondition.metric,
                operator: condition?.operator || defaultCondition.operator,
                value: condition?.value ?? "",
                period: condition?.period || defaultCondition.period
            })
        );
    }

    /*
     * Trigger
     */
    const triggerType = rule?.trigger?.type || "metrics";
    values.triggerType = triggerType;

    if (triggerType === "metrics") {
        const config = rule?.trigger?.config || {};
        const runWindow = config?.runWindow || {};
        values.intervalMinutes = Number(config?.intervalMinutes) || 720;
        values.metrics.runWindow.type = runWindow?.type || "always";

        if (runWindow?.type === "datetime_range") {
            values.metrics.runWindow.dateTimeRange = {
                from: runWindow?.startAt || "",
                to: runWindow?.endAt || ""
            };
        }

        if (runWindow?.type === "weekly_time_range") {
            values.schedule.days = mapTimeRangesToScheduleDays(runWindow?.timeRanges || []);
        }
    }

    if (triggerType === "schedule") {
        const parsed = parseScheduleCron(rule?.trigger?.config?.cron);
        values.schedule.cronDays = parsed.days;
        values.schedule.time = parsed.time;
    }

    /*
     * Actions
     */

    const actions = Array.isArray(rule?.actions) ? rule.actions : [];
    // Ignore legacy email actions when restoring the campaign action.
    const mainAction = actions.find((item) => item?.action !== "send_email");

    values.actions = [
        {
            action: mainAction?.action || defaultAction.action,
            parameters: {
                type: mainAction?.parameters?.type || "percent",
                value: mainAction?.parameters?.value ?? "",
                maxDailyBudget: mainAction?.parameters?.maxDailyBudget ?? "",
                minDailyBudget: mainAction?.parameters?.minDailyBudget ?? ""
            }
        }
    ];

    return values;
};

export function AutomationCreateRule(props) {

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const history = useHistory();
    const location = useLocation();

    const {ruleId} = useParams();
    const parsedRuleId = Number(ruleId);
    const isEdit = Number.isInteger(parsedRuleId) && parsedRuleId > 0;

    const {create, update, find, alertError, closeForm, getWorkspaceIDs} = props;

    const {plan} = props?.auth?.subscription || {};
    const {rerender, rule} = props?.automationRulesState || {};
    const {userWorkspaces} = props?.workspace || {};
    const {customEvent, fetchCustomEvents} = props;
    const metrics = useMemo(() => {
        const events = customEvent?.isDeleted === false ? customEvent.data || [] : [];
        return [
            ...METRICS,
            ...events.flatMap(({slotNumber, name}) => [
                {
                    value: `slot${slotNumber}_custom_event_revenue_sum`,
                    label: `${name} Revenue`
                },
                {
                    value: `slot${slotNumber}_custom_event_conversions_sum`,
                    label: `${name} Conversions`
                }
            ])
        ];
    }, [customEvent?.data, customEvent?.isDeleted]);

    useEffect(() => {
        fetchCustomEvents({isDeleted: false});
    }, [fetchCustomEvents]);

    const {register, handleSubmit, control, setValue, reset, formState: {errors, isSubmitting}} = useForm({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: createDefaultFormValues()
    });

    const busy = isSubmitting || !!props.automationRulesState?.isLoading;

    const {fields: conditionFields, append: appendCondition, remove: removeCondition} = useFieldArray({
        control,
        name: "conditions"
    });

    const metricsRunWindowType = useWatch({control, name: "metrics.runWindow.type"});
    const triggerType = useWatch({control, name: "triggerType"});
    const targetMode = useWatch({control, name: "targetMode"});
    const trafficSource = useWatch({control, name: "trafficSource"});
    const selectedActionValues = useWatch({control, name: "actions"});
    const timezone = useWatch({control, name: "metrics.timezone"});
    const adAccountId = useWatch({control, name: "metrics.adAccountId"});
    const currency = useWatch({control, name: "metrics.currency"});

    // Load the rule when editing
    useEffect(() => {
        if (!isEdit) {
            return;
        }
        find(parsedRuleId);
    }, [isEdit, parsedRuleId, find]);

    // Populate the form when Redux receives the rule:
    useEffect(() => {

        if (!isEdit || !rule) {
            return;
        }

        if (rule?.id && Number(rule.id) !== parsedRuleId) {
            return;
        }

        let cancelled = false;

        const loadRule = async () => {

            try {

                // Map everything that is already available
                // directly from the automation rule.
                const formValues = mapRuleToForm(rule, userWorkspaces || []);
                const trafficSourceId = Number(rule?.trafficSourceId || 0);

                if (trafficSourceId > 0) {
                    const trafficSourceResponse = await trafficSourceServices.find(trafficSourceId);
                    if (cancelled) {
                        return;
                    }

                    if (trafficSourceResponse) {
                        formValues.trafficSource = {
                            value: trafficSourceResponse.id,
                            label: trafficSourceResponse.name,
                            integrationId: trafficSourceResponse.integrationId,
                            workspaceId: trafficSourceResponse.workspaceId
                        };

                        // Resolve workspace from traffic source.
                        const workspace = (userWorkspaces || []).find((item) => Number(item?.workspaceId) === Number(trafficSourceResponse.workspaceId));
                        if (workspace) {
                            formValues.workspace = workspace;
                        }
                    }

                    /*
                     * ------------------------------------------------
                     * CAMPAIGNS
                     * ------------------------------------------------
                     *
                     * Only required when the rule targets selected
                     * campaigns.
                     */
                    if (rule?.targetMode === "selected" && Array.isArray(rule?.campaigns) && rule.campaigns.length > 0) {
                        const campaignIds = [...new Set(rule.campaigns
                            .map((item) => Number(typeof item === "object" ? item?.campaignId : item))
                            .filter((id) => Number.isInteger(id) && id > 0))];
                        const campaignNames = new Map();

                        // The campaign endpoint returns at most 50 records per request.
                        for (let index = 0; index < campaignIds.length; index += MAX_SELECTED_CAMPAIGNS) {
                            const ids = campaignIds.slice(index, index + MAX_SELECTED_CAMPAIGNS).join(",");
                            const campaignResponse = await campaignServices.read({campaignIds: ids});
                            if (cancelled) {
                                return;
                            }
                            (campaignResponse?.campaigns || []).forEach((campaign) => {
                                campaignNames.set(Number(campaign.id), campaign.name);
                            });
                        }

                        // Keep saved IDs even when the campaign is absent from the active list.
                        formValues.campaign = campaignIds.map((id) => ({
                            value: id,
                            label: campaignNames.get(id) || `Campaign #${id}`
                        }));
                    }
                }

                if (cancelled) {
                    return;
                }

                /*
                 * Populate RHF only after all edit data
                 * has been resolved.
                 */
                reset(formValues);

            } catch (error) {
                console.error("Failed to load automation rule:", error);
                if (!cancelled) {
                    alertError("Failed to load automation rule.");
                }
            }
        };

        loadRule();

        return () => {
            cancelled = true;
        };

    }, [isEdit, parsedRuleId, rule, userWorkspaces, reset, alertError]);

    const buildScheduleCron = (days, time) => {
        if (!Array.isArray(days) || !time) {
            return "";
        }

        const validDays = days.filter((day) => Number.isInteger(day) && day >= 0 && day <= 6);

        if (validDays.length === 0) {
            return "";
        }

        const [hour, minute] = time.split(":");
        if (hour === undefined || minute === undefined) {
            return "";
        }

        const order = [1, 2, 3, 4, 5, 6, 0];
        const sortedDays = [...validDays].sort((a, b) => order.indexOf(a) - order.indexOf(b));

        return `${Number(minute)} ${Number(hour)} * * ${sortedDays.join(",")}`;
    };

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick' || busy) return;
        history.push({pathname: "/automation", state: {from: location}});
    }, [history, location, busy]);

    useEffect(() => {
        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
        }
    }, [plan, alertError]);

    useEffect(() => {
        if (!trafficSource) {
            setValue("schedule.timezone", "");
            setValue("metrics.timezone", "");
            setValue("metrics.adAccountId", "");
            setValue("metrics.currency", "");
            return;
        }

        const integrationId = Number(trafficSource?.integrationId || 0);

        setValue("schedule.timezone", "");
        setValue("metrics.timezone", "");
        setValue("metrics.adAccountId", "");
        setValue("metrics.currency", "");

        if (integrationId <= 0) {
            return;
        }

        integrationService
            .findIntegration(integrationId)
            .then((result) => {
                if (!result || result.networkType !== "facebook") {
                    return;
                }

                result.integrationCredentials?.forEach((credential) => {
                    if (credential.keyName === "fb_ad_account_id" && credential.keyValue) {
                        setValue("metrics.adAccountId", credential.keyValue);
                    }

                    if (credential.keyName === "fb_ad_account_currency" && credential.keyValue) {
                        setValue("metrics.currency", credential.keyValue);
                    }

                    if (credential.keyName === "fb_ad_account_timezone" && credential.keyValue) {
                        setValue("schedule.timezone", credential.keyValue);
                        setValue("metrics.timezone", credential.keyValue);
                    }
                });
            })
            .catch((error) => {
                console.error("Failed to load integration:", error);
            });

        // Find workspace data
        userWorkspaces?.forEach((item) => {
            if (item?.workspaceId === trafficSource?.workspaceId) {
                setValue("workspace", item);
            }
        });

    }, [trafficSource, setValue, userWorkspaces]);

    const handleOnSubmit = async (data) => {

        /*
         * Convert the UI model into your backend AutomationRule
         * request model.
         */
        const payload = {
            name: data?.name?.trim(),
            trafficSourceId: Number(data?.trafficSource?.value),
            entityType: data?.entityType,
            targetMode: data?.targetMode,
            ruleLogic: data?.ruleLogic,
            campaignIds: data?.targetMode === "selected" ? data?.campaign?.map((item) => Number(item?.value)) : [],
            conditions: data?.triggerType === "metrics" ? data?.conditions?.map((condition) => ({
                metric: condition?.metric,
                operator: condition?.operator,
                value: Number(condition?.value),
                period: condition?.period
            })) : [],
            actions: buildAutomationActions(data?.actions?.[0]),
            settings: {
                ...data.settings,
                notifications: {
                    ...data.settings?.notifications,
                    inApp: data?.actions?.[0]?.action === "none" || Boolean(data.settings?.notifications?.inApp)
                }
            },
            trigger: {
                type: "",
                config: {}
            }
        };

        // trigger metrics cron
        if (data.triggerType === "metrics") {
            payload.trigger.type = "metrics";
            payload.trigger.config = {
                intervalMinutes: Number(data?.intervalMinutes),
                runWindow: {
                    type: data?.metrics?.runWindow?.type,
                }
            };

            if (data?.metrics?.runWindow?.type === "datetime_range") {
                payload.trigger.config.runWindow.startAt = data?.metrics?.runWindow?.dateTimeRange?.from;
                payload.trigger.config.runWindow.endAt = data?.metrics?.runWindow?.dateTimeRange?.to;
            }

            if (data?.metrics?.runWindow?.type === "weekly_time_range") {
                payload.trigger.config.runWindow.timeRanges = (data?.schedule?.days || []).flatMap((item) =>
                    item?.enabled ? (item?.ranges || []).map((range) => ({
                            weekDay: Number(item?.day),
                            startTime: range?.from,
                            endTime: range?.to,
                        }))
                        : []
                );
            }

        }

        // trigger schedule cron
        if (data?.triggerType === "schedule") {
            payload.conditions = []; // Override conditions. When trigger type is schedule then can not set conditions.
            payload.trigger = {
                type: "schedule",
                config: {
                    cron: buildScheduleCron(data?.schedule?.cronDays, data?.schedule?.time),
                },
            };
        }

        if (isEdit) {
            //console.log("Edit automation rule:", payload);
            await update({
                ruleId: parsedRuleId,
                formData: payload,
                trafficSourceName: data?.trafficSource?.label,
                workspaceName: data?.workspace?.name
            });
        } else {
            //console.log("Create automation rule:", payload);
            await create({
                formData: payload,
                trafficSourceName: data?.trafficSource?.label,
                workspaceName: data?.workspace?.name
            });
        }
    };

    useEffect(() => {
        if (rerender) {
            closeForm();
            handleClose();
        }
    }, [closeForm, handleClose, rerender])


    return (
        <AutomationDialog open onClose={handleClose} fullWidth fullScreen={fullScreen}
            aria-labelledby="automation-rule-dialog" maxWidth="md">
            <DialogTitle id="automation-rule-dialog">
                {isEdit ? 'Edit automation rule' : 'New automation rule'}
                <SettingsDialogDescription>Automate campaign controls using performance conditions or a schedule.</SettingsDialogDescription>
            </DialogTitle>
            {busy && <LinearProgress aria-label="Loading automation rule" sx={{mx: {xs: 2, sm: 3}, borderRadius: 1}}/>}
            <DialogContent>
                <Box component="form" id="automation-rule-form" onSubmit={handleSubmit(handleOnSubmit)} autoComplete="off">
                    <Box component="fieldset" disabled={busy} sx={{border: 0, p: 0, m: 0, minWidth: 0, display: 'grid', gap: 2}}>
                        <AutomationSection title="Rule details" description="Give your rule a recognizable name and choose an integrated Meta traffic source.">
                            <Box sx={automationFieldGrid}>
                                <TextField {...register('name', {
                                    required: 'Rule name is mandatory.',
                                    minLength: {value: 4, message: 'Rule name must contain at least 4 characters.'},
                                    maxLength: {value: 100, message: 'Rule name cannot exceed 100 characters.'}
                                })} error={!!errors.name} helperText={errors.name?.message}
                                    label="Rule name *" size="small" placeholder="Example: Pause expensive Meta ads" fullWidth
                                    slotProps={{inputLabel: {shrink: true}}}/>
                                <TrafficSourceSelect control={control} showFormLabel={false} showInputLabel
                                    validate={validateTrafficSource} workspace={getWorkspaceIDs()}
                                    slotProps={{paper: {sx: automationSelectPaperStyles}}}/>
                            </Box>
                            {timezone !== '' && adAccountId !== '' && <Box sx={{...automationFieldGrid, gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))'}}}>
                                {[['Ad account', adAccountId], ['Reporting timezone', timezone], ['Currency', currency]].map(([label, value]) =>
                                    <Box key={label} sx={{minWidth: 0}}><Typography sx={{fontSize: 11, color: 'text.secondary', mb: 0.5}}>{label}</Typography>
                                        <Typography sx={{fontSize: 13, fontWeight: 500, overflowWrap: 'anywhere'}}>{value}</Typography></Box>)}
                            </Box>}
                        </AutomationSection>

                        <AutomationSection title="Targets" description="Choose which entities and campaigns this rule controls.">
                            <Controller name="entityType" control={control} rules={{required: 'Entity type is required.'}}
                                render={({field}) => <FormControl fullWidth size="small" error={!!errors.entityType} sx={{maxWidth: 350}}>
                                    <InputLabel id="automation-entity-type-label">Entity type</InputLabel><Select {...field} label="Entity type" labelId="automation-entity-type-label" MenuProps={automationSelectMenuProps}>
                                        {ENTITY_TYPES.map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                                    </Select><FormHelperText>{errors.entityType?.message}</FormHelperText>
                                </FormControl>}/>
                            <Controller name="targetMode" control={control} render={({field}) => <FormControl>
                                <Typography sx={{fontSize: 13, fontWeight: 600, mb: 0.5}}>Apply this rule to</Typography>
                                <RadioGroup {...field} row>
                                    <FormControlLabel value="all" control={<Radio size="small"/>} label="All campaigns with traffic"/>
                                    <FormControlLabel value="selected" control={<Radio size="small"/>} label="Selected Skro campaigns"/>
                                </RadioGroup>
                            </FormControl>}/>
                            {targetMode === 'selected' && <CampaignSelect control={control} errors={errors}
                                trafficSourceId={trafficSource?.value || ''} slotProps={{paper: {sx: automationSelectPaperStyles}}}/>}
                        </AutomationSection>

                        <AutomationSection title="Trigger" description="Run when performance conditions match, or at specific times.">
                            <Controller name="triggerType" control={control} render={({field}) => <FormControl fullWidth size="small" sx={{maxWidth: 350}}>
                                <InputLabel id="automation-trigger-label">Trigger</InputLabel><Select {...field} label="Trigger" labelId="automation-trigger-label" MenuProps={automationSelectMenuProps}>
                                    <MenuItem value="metrics">When metric conditions are met</MenuItem>
                                    <MenuItem value="schedule">On a schedule</MenuItem>
                                </Select>
                            </FormControl>}/>
                        </AutomationSection>

                        {triggerType === 'metrics' && <>
                            <AutomationSection title="Conditions" description="Some metrics can be delayed. Allow a buffer in your conditions to avoid false positives.">
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Typography sx={{fontSize: 12, color: 'text.secondary'}}>{conditionFields.length} of 5 conditions</Typography>
                                    <Button type="button" variant="outlined" size="small" startIcon={<Add/>} disabled={conditionFields.length >= 5}
                                        onClick={() => appendCondition({...defaultCondition})}>Add condition</Button>
                                </Box>
                                {conditionFields.length > 1 && <Controller name="ruleLogic" control={control} render={({field}) =>
                                    <FormControl fullWidth size="small" sx={{maxWidth: 260}}>
                                        <InputLabel id="automation-rule-logic-label">Match conditions</InputLabel><Select {...field} label="Match conditions" labelId="automation-rule-logic-label" MenuProps={automationSelectMenuProps}>
                                            <MenuItem value="AND">Match ALL conditions (AND)</MenuItem>
                                            <MenuItem value="OR">Match ANY condition (OR)</MenuItem>
                                        </Select>
                                    </FormControl>}/>}
                                <Stack spacing={1.5}>
                                    {conditionFields.map((field, index) => <ConditionRow key={field.id} metrics={metrics} index={index}
                                        control={control} errors={errors} canDelete={conditionFields.length > 1} onDelete={() => removeCondition(index)}/>)}
                                </Stack>
                                {conditionFields.length >= 5 && <Typography sx={{fontSize: 12, color: 'text.secondary'}}>Maximum 5 conditions per automation rule.</Typography>}
                            </AutomationSection>
                            <AutomationSection title="Schedule" description="Set how often to check conditions and when this rule is allowed to run.">
                                <Controller name="intervalMinutes" control={control} rules={{required: true}} render={({field}) =>
                                    <FormControl fullWidth size="small" sx={{maxWidth: 350}}>
                                        <InputLabel id="automation-frequency-label">Check frequency</InputLabel><Select {...field} label="Check frequency" labelId="automation-frequency-label" MenuProps={automationSelectMenuProps}>
                                            {FREQUENCIES.map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                                        </Select>
                                    </FormControl>}/>
                                <Controller name="metrics.runWindow.type" control={control} render={({field}) => <FormControl>
                                    <Typography sx={{fontSize: 13, fontWeight: 600, mb: 0.5}}>Run window</Typography>
                                    <RadioGroup {...field} row>
                                        <FormControlLabel value="always" control={<Radio size="small"/>} label="Continuously"/>
                                        <FormControlLabel value="datetime_range" control={<Radio size="small"/>} label="Date range"/>
                                        <FormControlLabel value="weekly_time_range" control={<Radio size="small"/>} label="Weekly time range"/>
                                    </RadioGroup>
                                </FormControl>}/>
                                {metricsRunWindowType === 'datetime_range' && <DateTimeRangePicker control={control} setValue={setValue}/>}
                                {metricsRunWindowType === 'weekly_time_range' && <ScheduleSettings control={control}/>}
                            </AutomationSection>
                        </>}
                        {triggerType === 'schedule' && <AutomationSection title="Schedule" description="Choose the weekdays and time when this rule should run.">
                            <OnAScheduleSettings control={control}/>
                        </AutomationSection>}
                        <AutomationSection title="Actions" description="Choose what happens when this rule runs.">
                            <ActionRow index={0} control={control} setValue={setValue} errors={errors}
                                action={selectedActionValues?.[0]?.action} canDelete={false}/>
                        </AutomationSection>
                        <NotificationSettings control={control} setValue={setValue} action={selectedActionValues?.[0]?.action}/>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button type="button" color="inherit" disabled={busy} onClick={handleClose}>Cancel</Button>
                <Button type="submit" form="automation-rule-form" variant="contained" disabled={busy}
                    startIcon={busy ? <CircularProgress size={16} color="inherit"/> : undefined}>
                    {busy ? 'Please wait…' : isEdit ? 'Save changes' : 'Create rule'}
                </Button>
            </DialogActions>
        </AutomationDialog>
    );
}

const validateTrafficSource = async (trafficSource) => {
    if (!trafficSource) {
        return "Traffic source is required.";
    }

    const integrationId = Number(trafficSource?.integrationId || 0);

    if (integrationId <= 0) {
        return "Selected traffic source isn't integrated.";
    }

    try {
        const result = await integrationService.findIntegration(integrationId);

        if (!result) {
            return "Integration not found.";
        }

        if (result.networkType !== "facebook") {
            return "Invalid integration.";
        }

        return true;

    } catch (error) {
        return "Failed to load integration.";
    }
};

function ConditionRow({index, control, errors, canDelete, onDelete, metrics}) {

    const conditionError = errors?.conditions?.[index];

    return (
        <Box className="automation-nested-inputs" sx={{p: 1.5, borderRadius: '10px', minWidth: 0, bgcolor: theme => getSettingsColors(theme).card}}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, minHeight: 28}}>
                <Typography sx={{fontSize: 12, fontWeight: 600, color: 'text.secondary'}}>Condition {index + 1}</Typography>
                {canDelete && <IconButton type="button" size="small" color="error" aria-label={`Remove condition ${index + 1}`} onClick={onDelete}>
                    <DeleteOutline fontSize="small"/>
                </IconButton>}
            </Box>
            <Box sx={{display: 'grid', gap: 1.5, minWidth: 0,
                '& .MuiSelect-select': {pl: 1, pr: '28px !important'},
                gridTemplateColumns: {xs: 'repeat(2, minmax(0, 1fr))', md: 'minmax(0, 1.2fr) minmax(0, 1.3fr) minmax(0, 0.8fr) minmax(0, 1fr)'}, alignItems: 'start'}}>
                <Controller
                    name={`conditions.${index}.metric`}
                    control={control}
                    rules={{required: "Metric is required."}}
                    render={({field}) => (
                        <FormControl size="small" sx={{minWidth: 0}} error={!!conditionError?.metric}>
                            <InputLabel id={`automation-condition-${index}-metric-label`}>Metric</InputLabel>
                            <Select {...field} label="Metric" labelId={`automation-condition-${index}-metric-label`} MenuProps={automationSelectMenuProps}>
                                {metrics.map((metric) => (
                                    <MenuItem key={metric.value} value={metric.value}>
                                        {metric.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />

                <Controller
                    name={`conditions.${index}.operator`}
                    control={control}
                    rules={{required: "Operator is required."}}
                    render={({field}) => (
                        <FormControl size="small" sx={{minWidth: 0}}>
                            <InputLabel id={`automation-condition-${index}-operator-label`}>Operator</InputLabel>
                            <Select {...field} label="Operator" labelId={`automation-condition-${index}-operator-label`} MenuProps={automationSelectMenuProps}>
                                {OPERATORS.map((operator) => (
                                    <MenuItem key={operator.value} value={operator.value}>
                                        {operator.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />

                <Controller
                    name={`conditions.${index}.value`}
                    control={control}
                    rules={{
                        required: "Value is required.",
                        validate: (value) => {
                            if (value === "" || value === null || value === undefined) {
                                return "Value is required.";
                            }
                            if (Number.isNaN(Number(value))) {
                                return "Enter a valid number.";
                            }
                            return true;
                        }
                    }}
                    render={({field}) => (
                        <TextField
                            {...field}
                            size="small"
                            type="number"
                            label="Value"
                            error={!!conditionError?.value}
                            helperText={conditionError?.value?.message}
                            sx={{minWidth: 0}}
                            placeholder="0"
                            slotProps={{
                                htmlInput: {
                                    step: "any"
                                },
                                inputLabel: {
                                    shrink: true
                                }
                            }}
                        />
                    )}
                />

                <Controller
                    name={`conditions.${index}.period`}
                    control={control}
                    rules={{required: "Period is required."}}
                    render={({field}) => (
                        <FormControl size="small" sx={{minWidth: 0}}>
                            <InputLabel id={`automation-condition-${index}-period-label`}>Period</InputLabel>
                            <Select {...field} label="Period" labelId={`automation-condition-${index}-period-label`} MenuProps={automationSelectMenuProps}>
                                {PERIODS.map((period) => (
                                    <MenuItem key={period.value} value={period.value}>
                                        {period.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />

            </Box>
        </Box>
    );
}

function ActionRow({index, control, setValue, errors, action, canDelete, onDelete}) {

    const actionError = errors?.actions?.[index];
    const isBudgetAction = action === "increase_daily_budget" || action === "decrease_daily_budget";
    const isIncreaseDailyBudgetAction = action === "increase_daily_budget";
    const isDecreaseDailyBudgetAction = action === "decrease_daily_budget";
    const entityType = useWatch({control, name: "entityType"});
    const availableActions = ACTIONS.filter((item) => {
        if (entityType === "ad") {
            return !["increase_daily_budget", "decrease_daily_budget",].includes(item.value);
        }

        return true;
    });

    // if the user first selects increase_daily_budget and then changes the entity type to Meta ad, the old invalid
    // action can remain in the form value. It will clear it automatically.
    useEffect(() => {
        const invalidActions = [
            "increase_daily_budget",
            "decrease_daily_budget",
        ];

        if (entityType === "ad" && invalidActions.includes(action)) {
            setValue(`actions.${index}.action`, defaultAction.action,
                {
                    shouldDirty: true,
                    shouldValidate: true,
                }
            );
        }
    }, [entityType, action, index, setValue]);

    return (
        <Box>
            <Box sx={{display: 'grid', gap: 1.5, minWidth: 0, alignItems: 'start',
                gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: isBudgetAction ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 350px)',
                    md: isBudgetAction ? 'minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 0.8fr) minmax(0, 1.2fr)' : 'minmax(0, 350px)'}}}>
                <Controller
                    name={`actions.${index}.action`}
                    control={control}
                    rules={{
                        required: "Action is required."
                    }}
                    render={({field}) => (
                        <FormControl
                            fullWidth
                            size="small"
                            error={!!actionError?.action}
                            sx={{minWidth: 0}}
                        >
                            <InputLabel id={`automation-action-${index}-label`}>Action</InputLabel>
                            <Select {...field} label="Action" labelId={`automation-action-${index}-label`} MenuProps={automationSelectMenuProps}>
                                {availableActions.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {actionError?.action?.message && (
                                <FormHelperText>
                                    {actionError.action.message}
                                </FormHelperText>
                            )}
                        </FormControl>
                    )}
                />

                {isBudgetAction && (
                    <>
                        <Controller
                            name={`actions.${index}.parameters.type`}
                            control={control}
                            defaultValue="percent"
                            render={({field}) => (
                                <FormControl size="small" sx={{minWidth: 0}}>
                                    <InputLabel id={`automation-action-${index}-unit-label`}>Change by</InputLabel>
                                    <Select {...field} label="Change by" labelId={`automation-action-${index}-unit-label`} MenuProps={automationSelectMenuProps}>
                                        <MenuItem value="percent">
                                            Percent
                                        </MenuItem>
                                        <MenuItem value="fixed">
                                            Fixed amount
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                        />

                        <Controller
                            name={`actions.${index}.parameters.value`}
                            control={control}
                            rules={{
                                required: {
                                    value: isBudgetAction,
                                    message: "Budget value is required."
                                },
                                validate: (value) => {
                                    if (!isBudgetAction) {
                                        return true;
                                    }
                                    return (
                                        Number(value) > 0 ||
                                        "Value must be greater than 0."
                                    );
                                }
                            }}
                            render={({field}) => (
                                <TextField
                                    {...field}
                                    label="Value"
                                    size="small"
                                    type="number"
                                    error={!!actionError?.parameters?.value}
                                    helperText={actionError?.parameters?.value?.message}
                                    sx={{minWidth: 0}}
                                    placeholder="0"
                                    slotProps={{
                                        htmlInput: {
                                            min: 0,
                                            step: "any"
                                        },
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            )}
                        />

                        {isIncreaseDailyBudgetAction && (
                            <Controller
                                name={`actions.${index}.parameters.maxDailyBudget`}
                                control={control}
                                rules={{
                                    required: {
                                        value: isIncreaseDailyBudgetAction,
                                        message: "Max daily budget value is required."
                                    },
                                    validate: (value) => {
                                        if (!isIncreaseDailyBudgetAction) {
                                            return true;
                                        }
                                        return (
                                            Number(value) > 0 ||
                                            "Max daily budget must be greater than 0."
                                        );
                                    }
                                }}
                                render={({field}) => (
                                    <TextField
                                        {...field}
                                        label="Maximum daily budget"
                                        size="small"
                                        type="number"
                                        error={!!actionError?.parameters?.maxDailyBudget}
                                        helperText={actionError?.parameters?.maxDailyBudget?.message}
                                        sx={{minWidth: 0}}
                                        placeholder="0"
                                        slotProps={{
                                            htmlInput: {
                                                min: 0,
                                                step: "any"
                                            },
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                )}
                            />
                        )}

                        {isDecreaseDailyBudgetAction && (
                            <Controller
                                name={`actions.${index}.parameters.minDailyBudget`}
                                control={control}
                                rules={{
                                    required: {
                                        value: isDecreaseDailyBudgetAction,
                                        message: "Min daily budget value is required."
                                    },
                                    validate: (value) => {
                                        if (!isDecreaseDailyBudgetAction) {
                                            return true;
                                        }
                                        return (
                                            Number(value) > 0 ||
                                            "Min daily budget must be greater than 0."
                                        );
                                    }
                                }}
                                render={({field}) => (
                                    <TextField
                                        {...field}
                                        label="Minimum daily budget"
                                        size="small"
                                        type="number"
                                        error={!!actionError?.parameters?.minDailyBudget}
                                        helperText={actionError?.parameters?.minDailyBudget?.message}
                                        sx={{minWidth: 0}}
                                        placeholder="0"
                                        slotProps={{
                                            htmlInput: {
                                                min: 0,
                                                step: "any"
                                            },
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                )}
                            />
                        )}
                    </>
                )}

                {canDelete && (
                    <Button
                        type="button"
                        color="error"
                        onClick={onDelete}
                        sx={{minWidth: 40, height: 40}}
                    >
                        <DeleteOutline/>
                    </Button>
                )}
            </Box>

        </Box>
    );
}

function NotificationSettings({control, setValue, action}) {
    const isSendNotificationOnly = action === 'none';

    // Notification-only rules must keep at least one notification channel enabled.
    useEffect(() => {
        if (isSendNotificationOnly) {
            setValue('settings.notifications.inApp', true, {shouldDirty: true});
        }
    }, [setValue, isSendNotificationOnly]);

    return <AutomationSection title="Notifications" description="Receive rule updates in your workspace’s notification inbox.">
        <Controller name="settings.notifications.inApp" control={control}
            render={({field}) => <FormControlLabel label="In-app notification" labelPlacement="start"
                sx={{m: 0, width: '100%', justifyContent: 'space-between', gap: 2}}
                control={<Switch name={field.name} inputRef={field.ref} onBlur={field.onBlur}
                    checked={!!field.value} disabled={isSendNotificationOnly}
                    onChange={event => field.onChange(event.target.checked)}/>}/>}/>
        {isSendNotificationOnly && <Typography sx={{fontSize: 13, color: 'text.secondary', mt: 0.5}}>
            In-app notifications are required for notification-only rules.
        </Typography>}
    </AutomationSection>;
}

const weekDays = [
    {label: "Mon", value: 1},
    {label: "Tue", value: 2},
    {label: "Wed", value: 3},
    {label: "Thu", value: 4},
    {label: "Fri", value: 5},
    {label: "Sat", value: 6},
    {label: "Sun", value: 0},
];

function OnAScheduleSettings({control}) {
    return (
        <Stack spacing={1}>
            <Controller
                name="schedule.cronDays"
                control={control}
                defaultValue={[]}
                rules={{
                    validate: (value) =>
                        value?.length > 0 ||
                        "Select at least one weekday.",
                }}
                render={({field, fieldState}) => (
                    <FormControl error={!!fieldState.error}>
                        <Typography
                            variant="body1"
                            sx={{mb: 1}}
                        >
                            Run on
                        </Typography>

                        <FormGroup row sx={{gap: 0.5, '& .MuiFormControlLabel-root': {ml: 0, mr: 1.5}}}>
                            {weekDays.map((day) => (
                                <FormControlLabel
                                    key={day.value}
                                    label={day.label}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={
                                                field.value?.includes(day.value) ||
                                                false
                                            }
                                            onChange={(event) => {
                                                const current = field.value || [];

                                                if (event.target.checked) {
                                                    field.onChange([
                                                        ...current,
                                                        day.value,
                                                    ]);
                                                } else {
                                                    field.onChange(
                                                        current.filter(
                                                            (value) =>
                                                                value !== day.value
                                                        )
                                                    );
                                                }
                                            }}
                                        />
                                    }
                                />
                            ))}
                        </FormGroup>

                        {fieldState.error?.message && (
                            <FormHelperText>
                                {fieldState.error.message}
                            </FormHelperText>
                        )}
                    </FormControl>
                )}
            />

            <Controller
                name="schedule.time"
                control={control}
                defaultValue=""
                rules={{
                    required: "Time is required.",
                }}
                render={({field, fieldState}) => (
                    <TextField
                        {...field}
                        type="time"
                        size="small"
                        label="Time"
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                            htmlInput: {
                                step: 60,
                            },
                        }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        sx={{
                            maxWidth: 180,

                            "& input[type='time']": {colorScheme: theme => theme.palette.mode},
                        }}
                    />
                )}
            />
        </Stack>
    );
}

const DAYS = [
    {value: 1, label: "Monday"},
    {value: 2, label: "Tuesday"},
    {value: 3, label: "Wednesday"},
    {value: 4, label: "Thursday"},
    {value: 5, label: "Friday"},
    {value: 6, label: "Saturday"},
    {value: 0, label: "Sunday"},
];

const TIMES = Array.from({length: 49}, (_, index) => {
    const hour = Math.floor(index / 2);
    const minute = index % 2 === 0 ? 0 : 30;

    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
});

const DEFAULT_SCHEDULE = DAYS.map((day) => ({
    day: day.value,
    enabled: false,
    ranges: [
        {
            from: "00:00",
            to: "24:00",
        },
    ],
}));

function ScheduleSettings({control}) {
    return (
        <Controller
            name="schedule.days"
            control={control}
            defaultValue={DEFAULT_SCHEDULE}
            render={({field}) => (
                <ScheduleDays
                    value={field.value || DEFAULT_SCHEDULE}
                    onChange={field.onChange}
                />
            )}
        />
    );
}

function ScheduleDays({value, onChange}) {

    const updateDay = (dayValue, callback) => {
        onChange(
            value.map((day) =>
                day.day === dayValue ? callback(day) : day
            )
        );
    };

    const toggleDay = (dayValue) => {
        updateDay(dayValue, (day) => ({
            ...day,
            enabled: !day.enabled,
        }));
    };

    const updateRange = (dayValue, rangeIndex, key, newValue) => {
        updateDay(dayValue, (day) => ({
            ...day,
            ranges: day.ranges.map((range, index) =>
                index === rangeIndex
                    ? {
                        ...range,
                        [key]: newValue,
                    }
                    : range
            ),
        }));
    };

    const addRange = (dayValue) => {
        updateDay(dayValue, (day) => ({
            ...day,
            enabled: true,
            ranges: [
                ...day.ranges,
                {
                    from: "00:00",
                    to: "24:00",
                },
            ],
        }));
    };

    const removeRange = (dayValue, rangeIndex) => {
        updateDay(dayValue, (day) => ({
            ...day,
            ranges: day.ranges.filter(
                (_, index) => index !== rangeIndex
            ),
        }));
    };

    return <Box sx={{display: 'grid', gap: 1.5, width: '100%', minWidth: 0}}>
        {DAYS.map(dayConfig => {
            const day = value.find(item => item.day === dayConfig.value);
            if (!day) return null;
            return <Box key={day.day} className="automation-nested-inputs" sx={{display: 'grid', gap: 1, alignItems: 'start', p: 1.5, borderRadius: '10px',
                minWidth: 0, bgcolor: theme => getSettingsColors(theme).card,
                gridTemplateColumns: {xs: '28px minmax(0, 1fr) 32px', sm: '28px 100px minmax(0, 1fr) 32px'}}}>
                <Checkbox size="small" checked={day.enabled} onChange={() => toggleDay(day.day)}
                    slotProps={{input: {'aria-label': `Enable ${dayConfig.label}`}}} sx={{p: 0.5, mt: 0.25}}/>
                <Typography sx={{fontSize: 13, pt: '7px', color: day.enabled ? 'text.primary' : 'text.secondary'}}>{dayConfig.label}</Typography>
                <Box sx={{display: 'grid', gap: 1, minWidth: 0, gridArea: {xs: '2 / 1 / 3 / 4', sm: '1 / 3 / 2 / 4'}}}>
                    {day.ranges.map((range, rangeIndex) => <Box key={`${day.day}-${rangeIndex}`} sx={{display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) 16px minmax(0, 1fr) 28px', alignItems: 'center', gap: 1, minWidth: 0}}>
                        <TimeSelect value={range.from} disabled={!day.enabled} label={`${dayConfig.label} start time ${rangeIndex + 1}`}
                            onChange={newValue => updateRange(day.day, rangeIndex, 'from', newValue)}/>
                        <Typography color="text.secondary" sx={{fontSize: 12, textAlign: 'center'}}>–</Typography>
                        <TimeSelect value={range.to} disabled={!day.enabled} label={`${dayConfig.label} end time ${rangeIndex + 1}`}
                            onChange={newValue => updateRange(day.day, rangeIndex, 'to', newValue)}/>
                        <Box sx={{width: 28, height: 32}}>{rangeIndex > 0 && <IconButton type="button" size="small" color="error"
                            aria-label={`Remove ${dayConfig.label} time range ${rangeIndex + 1}`} onClick={() => removeRange(day.day, rangeIndex)}>
                            <DeleteOutline fontSize="small"/>
                        </IconButton>}</Box>
                    </Box>)}
                </Box>
                <IconButton type="button" size="small" disabled={!day.enabled} onClick={() => addRange(day.day)}
                    aria-label={`Add time range for ${dayConfig.label}`} sx={{width: 32, height: 32, color: 'primary.main',
                        gridArea: {xs: '1 / 3 / 2 / 4', sm: '1 / 4 / 2 / 5'}}}><Add fontSize="small"/></IconButton>
            </Box>;
        })}
    </Box>;
}

function TimeSelect({value, onChange, disabled, label}) {

    return (
        <FormControl
            size="small"
            sx={{
                width: "100%",
                minWidth: 0,
                '& .MuiSelect-select': {pl: 1, pr: '26px !important', fontSize: 12},
                '& .MuiSelect-icon': {fontSize: 18, right: 4},
            }}
        >
            <Select
                value={value}
                disabled={disabled}
                inputProps={{'aria-label': label}}
                onChange={(event) =>
                    onChange(event.target.value)
                }
                MenuProps={{
                    PaperProps: {
                        sx: {
                            ...automationSelectPaperStyles, maxHeight: 300,
                        },
                    },
                }}
            >
                {TIMES.map((time) => (
                    <MenuItem
                        key={time}
                        value={time}
                        sx={{
                            fontSize: 13,
                        }}
                    >
                        {time}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

const buildAutomationActions = (formAction) => {
    const actions = [];

    // Main action
    const mainAction = {
        action: formAction.action,
        executionOrder: 1,
        parameters: {}
    };

    if (formAction.action === "increase_daily_budget" || formAction.action === "decrease_daily_budget") {
        mainAction.parameters = {
            type: formAction.parameters?.type || "percent",
            value: Number(formAction.parameters?.value),
        };
        if (formAction.action === "increase_daily_budget") {
            mainAction.parameters.maxDailyBudget = Number(formAction.parameters?.maxDailyBudget);
        }
        if (formAction.action === "decrease_daily_budget") {
            mainAction.parameters.minDailyBudget = Number(formAction.parameters?.minDailyBudget);
        }
    }
    actions.push(mainAction);

    return actions;
};

const mapStateToProps = (state) => {
    return {
        automationRulesState: state.automationRules,
        alert: state.alert,
        customEvent: state.customEvent,
        workspace: state.workspace,
        auth: state.auth
    }
};

const mapDispatchToProps = {
    fetchCustomEvents: customEventActions.fetch,
    find: automationRulesAction.findRule,
    create: automationRulesAction.createRule,
    update: automationRulesAction.updateRule,
    closeForm: automationRulesAction.closeForm,
    stopLoading: automationRulesAction.stopLoading,
    alertError: alertActions.error,
    getWorkspaceIDs: workspaceActions.getWorkspaceIDs,
};

export default connect(mapStateToProps, mapDispatchToProps)(AutomationCreateRule);
